import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import { Building2, Mail } from 'lucide-react';

export default function Login() {
  const [azureLoading, setAzureLoading] = useState(false);
  const [showInactiveAlert, setShowInactiveAlert] = useState(false);
  const { signInWithAzure, user } = useAuthContext();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Redirecionar se o usuário já estiver autenticado, com delay para garantir renderização completa
  useEffect(() => {
    // Verificar após um pequeno delay para garantir que a página carregou completamente
    const timer = setTimeout(() => {
      if (user) {
        console.log('✅ Login: Usuário já autenticado, redirecionando para home');
        navigate('/');
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [user, navigate]);

  // Verificar a conexão com o Supabase ao carregar a página - apenas uma vez
  useEffect(() => {
    async function checkSupabaseConnection() {
      try {
        console.log('Verificando conexão com o Supabase...');
        console.log('URL do Supabase:', import.meta.env.VITE_SUPABASE_URL);
        
        // Log apenas para debug - não vamos chamar getSession repetidamente
      } catch (error) {
        console.error('Erro ao verificar a conexão com o Supabase:', error);
      }
    }
    
    // Executa apenas uma vez, independente das props
    checkSupabaseConnection();
  }, []);

  // Remover margens e paddings do body e html para eliminar bordas brancas
  useEffect(() => {
    const originalBodyStyle = document.body.style.cssText;
    const originalHtmlStyle = document.documentElement.style.cssText;
    
    // Aplica estilos de sangria diretamente no body
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.backgroundColor = '#1F1746';
    document.body.style.backgroundImage = 'url(/elemento-01.svg)';
    document.body.style.backgroundSize = '200%';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.overflowX = 'hidden'; // Impede a barra de rolagem horizontal
    document.body.style.overflowY = 'hidden'; // Impede a barra de rolagem vertical

    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    
    return () => {
      // Restaura os estilos originais
      document.body.style.cssText = originalBodyStyle;
      document.documentElement.style.cssText = originalHtmlStyle;
    };
  }, []);

  // Função para login com Azure
  const handleAzureLogin = async () => {
    if (azureLoading) {
      console.log('🚫 Azure login already in progress, ignoring submission');
      return;
    }
    
    setAzureLoading(true);
    setShowInactiveAlert(false);

    try {
      console.log('🔵 Iniciando login com Azure...');
      await signInWithAzure();
      
      toast({
        title: "Redirecionando...",
        description: "Você será redirecionado para o Microsoft Azure para autenticação.",
      });
      
    } catch (error: any) {
      console.error('❌ Azure login error:', error);
      
      // Check if it's an inactive user error
      const isInactiveUserError = error?.message?.toLowerCase().includes('inactive') || 
                                 error?.message?.toLowerCase().includes('disabled') ||
                                 error?.message?.toLowerCase().includes('blocked');
      
      if (isInactiveUserError) {
        setShowInactiveAlert(true);
        toast({
          variant: "destructive",
          title: "Conta inativa",
          description: "Sua conta foi desativada. Entre em contato com o administrador.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Erro no login Azure",
          description: error?.message || "Erro ao iniciar autenticação com Microsoft Azure. Tente novamente.",
        });
      }
    } finally {
      setAzureLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen w-screen flex items-center justify-center p-0 m-0 relative overflow-hidden" 
    >
      <Card className="w-full max-w-md border-0 shadow-none">
        <CardHeader className="text-center">
          {/* Logo da Trivium */}
          <div className="flex justify-center mb-6">
            <img 
              src="https://cdn.brandfetch.io/idNdb_MPJ2/w/4500/h/1145/theme/dark/logo.png?c=1dxbfHSJFAPEGdCLU4o5B" 
              alt="Trivium Logo" 
              className="h-12 object-contain"
            />
          </div>
          <CardTitle>Customer Service Management</CardTitle>
          <CardDescription>
            Entre com sua conta para acessar o sistema.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {showInactiveAlert && (
            <Alert variant="destructive">
              <AlertDescription>
                Sua conta foi desativada. Entre em contato com o administrador.
              </AlertDescription>
            </Alert>
          )}

          {/* Botão de Login com Azure */}
          <Button 
            onClick={handleAzureLogin}
            variant="outline" 
            className="w-full bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
            disabled={azureLoading}
          >
            {azureLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span>Conectando...</span>
              </div>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Entrar com Microsoft 365
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
} 